import { codeExample } from './demo-shared.js';
import { html } from 'lit';
import type { DemoSection } from './demo-shared.js';

export const inputSection: DemoSection = {
  id: 'input',
  title: 'Input',
  tags: ['webmodule-input'],
  summary: 'Single-line text entry with sizes, native input types, affix slots and validation states.',
  render: () => html`
    ${codeExample({
      label: 'Sizes',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-input size="small" placeholder="Small"></webmodule-input>
          <webmodule-input size="medium" placeholder="Medium"></webmodule-input>
          <webmodule-input size="large" placeholder="Large"></webmodule-input>
          <webmodule-input size="medium" placeholder="Pill" pill></webmodule-input>
        </div>`
    })}
    ${codeExample({
      label: 'Input types',
      description: 'The type attribute forwards to the native input, so browser widgets come for free.',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-input type="number" placeholder="number" size="small"></webmodule-input>
          <webmodule-input type="password" placeholder="password" password-toggle size="small"></webmodule-input>
          <webmodule-input type="date" size="small"></webmodule-input>
          <webmodule-input type="datetime-local" size="small"></webmodule-input>
          <webmodule-input type="time" size="small"></webmodule-input>
        </div>`
    })}
    ${codeExample({
      label: 'Prefix, suffix and clearable',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-input placeholder="Amount" size="small">
            <span slot="prefix">$</span>
            <span slot="suffix">USD</span>
          </webmodule-input>
          <webmodule-input placeholder="Clearable" size="small" clearable value="Clear me"></webmodule-input>
          <webmodule-input placeholder="With icon" size="small">
            <webmodule-icon slot="prefix" name="person"></webmodule-icon>
          </webmodule-input>
        </div>`
    })}
    ${codeExample({
      label: 'Labels and states',
      description: html`Labels render above the control by default. The <code>webmodule-control-left-label</code> class
        is an app-level pattern that moves the label into a fixed left column by restyling the exported
        <code>form-control</code> parts.`,
      code: `
        <div style="max-width: 520px;">
          <webmodule-input label="Label on top" placeholder="Preferred name" size="small"></webmodule-input>
          <br />
          <webmodule-input class="webmodule-control-left-label" label="Left label:" placeholder="Preferred name" size="small"></webmodule-input>
          <webmodule-input class="webmodule-control-left-label" label="Required:" placeholder="Must be filled" size="small" required></webmodule-input>
          <webmodule-input class="webmodule-control-left-label" label="Filled:" placeholder="Filled style" size="small" filled></webmodule-input>
          <webmodule-input class="webmodule-control-left-label" label="Disabled:" placeholder="Disabled" size="small" disabled></webmodule-input>
        </div>`
    })}
  `
};

export const inputMoneySection: DemoSection = {
  id: 'input-money',
  title: 'Input Money',
  tags: ['webmodule-input-money'],
  summary:
    'A currency-aware input: values format with thousands separators and a currency indicator while focused editing stays numeric.',
  render: () => html`
    ${codeExample({
      label: 'Sizes and values',
      code: `
        <div class="demo-stack" style="max-width: 420px;">
          <webmodule-input-money size="small" value="1234567.34"></webmodule-input-money>
          <webmodule-input-money size="medium" value="1234567.34"></webmodule-input-money>
          <webmodule-input-money size="large" value="1234567.34"></webmodule-input-money>
          <webmodule-input-money size="small" value="-1234567.34"></webmodule-input-money>
        </div>`
    })}
    ${codeExample({
      label: 'Currency and display style',
      description: html`<code>currency</code> accepts any ISO 4217 code (default USD);
        <code>currency-display</code> switches between symbol, narrow symbol, code and name.`,
      code: `
        <div style="max-width: 520px;">
          <webmodule-input-money class="webmodule-control-left-label" label="symbol" size="small" value="123" currency-display="symbol"></webmodule-input-money>
          <webmodule-input-money class="webmodule-control-left-label" label="narrowSymbol" size="small" value="123" currency-display="narrowSymbol"></webmodule-input-money>
          <webmodule-input-money class="webmodule-control-left-label" label="code" size="small" value="123" currency-display="code"></webmodule-input-money>
          <webmodule-input-money class="webmodule-control-left-label" label="name" size="small" value="123" currency-display="name"></webmodule-input-money>
          <webmodule-input-money class="webmodule-control-left-label" label="NZD" size="small" value="123" currency="NZD"></webmodule-input-money>
          <webmodule-input-money class="webmodule-control-left-label" label="ZAR" size="small" value="123" currency="ZAR"></webmodule-input-money>
        </div>`
    })}
  `
};

export const inputRowSection: DemoSection = {
  id: 'input-row',
  title: 'Input Row',
  tags: ['webmodule-input-row'],
  summary:
    'A layout wrapper that gives arbitrary content the same label, prefix and suffix chrome as a real form control, ' +
    'so composed rows line up with neighboring inputs.',
  render: () => html`
    ${codeExample({
      label: 'Composing controls in one labelled row',
      code: `
        <div style="max-width: 520px;">
          <webmodule-input-row label="Dimensions" size="small">
            <div class="demo-row" style="flex-wrap: nowrap;">
              <webmodule-input type="number" placeholder="Width" size="small"></webmodule-input>
              <span>&times;</span>
              <webmodule-input type="number" placeholder="Height" size="small"></webmodule-input>
              <span>mm</span>
            </div>
          </webmodule-input-row>
        </div>`
    })}
  `
};

export const textareaSection: DemoSection = {
  id: 'textarea',
  title: 'Textarea',
  tags: ['webmodule-textarea'],
  summary: 'Multi-line text entry sharing the input family sizing, label placement and fill styles.',
  render: () => html`
    ${codeExample({
      label: 'Sizes',
      code: `
        <div class="demo-stack" style="max-width: 520px;">
          <webmodule-textarea size="small" placeholder="Small"></webmodule-textarea>
          <webmodule-textarea size="medium" placeholder="Medium"></webmodule-textarea>
          <webmodule-textarea size="large" placeholder="Large"></webmodule-textarea>
        </div>`
    })}
    ${codeExample({
      label: 'Labels and states',
      code: `
        <div style="max-width: 520px;">
          <webmodule-textarea label="Label on top" placeholder="Notes" size="small"></webmodule-textarea>
          <br />
          <webmodule-textarea class="webmodule-control-left-label" label="Left label:" placeholder="Notes" size="small"></webmodule-textarea>
          <webmodule-textarea class="webmodule-control-left-label" label="Filled:" placeholder="Filled" size="small" filled></webmodule-textarea>
          <webmodule-textarea class="webmodule-control-left-label" label="Disabled:" placeholder="Disabled" size="small" disabled></webmodule-textarea>
          <webmodule-textarea class="webmodule-control-left-label" label="No resize:" placeholder="resize handle removed" size="small" resize="none"></webmodule-textarea>
        </div>`
    })}
  `
};

export const checkboxSection: DemoSection = {
  id: 'checkbox',
  title: 'Checkbox',
  tags: ['webmodule-checkbox'],
  summary: 'A tri-state checkbox: unchecked, checked, or indeterminate for partially-selected parents.',
  render: () => html`
    ${codeExample({
      label: 'Sizes',
      code: `
        <div class="demo-stack">
          <webmodule-checkbox size="small">Small</webmodule-checkbox>
          <webmodule-checkbox size="medium">Medium</webmodule-checkbox>
          <webmodule-checkbox size="large">Large</webmodule-checkbox>
        </div>`
    })}
    ${codeExample({
      label: 'States',
      code: `
        <div class="demo-stack">
          <webmodule-checkbox>Unchecked</webmodule-checkbox>
          <webmodule-checkbox checked>Checked</webmodule-checkbox>
          <webmodule-checkbox indeterminate>Indeterminate</webmodule-checkbox>
          <webmodule-checkbox disabled>Disabled</webmodule-checkbox>
          <webmodule-checkbox checked disabled>Checked + disabled</webmodule-checkbox>
        </div>`
    })}
  `
};

export const radioSection: DemoSection = {
  id: 'radio',
  title: 'Radio Group',
  tags: ['webmodule-radio-group', 'webmodule-radio', 'webmodule-radio-button'],
  summary:
    'Single-choice selection. Slot webmodule-radio elements for the classic look or webmodule-radio-button for a ' +
    'segmented button strip - the group manages value, name and keyboard navigation either way.',
  render: () => html`
    ${codeExample({
      label: 'Radios',
      code: `
        <div class="demo-stack">
          <webmodule-radio-group label="Choice of fruit" name="fruit-a" value="1" size="small">
            <webmodule-radio value="1">Apple</webmodule-radio>
            <webmodule-radio value="2">Orange</webmodule-radio>
            <webmodule-radio value="3">Grape</webmodule-radio>
          </webmodule-radio-group>
          <webmodule-radio-group label="Inline" name="fruit-b" value="1" inline size="small">
            <webmodule-radio value="1">Apple</webmodule-radio>
            <webmodule-radio value="2">Orange</webmodule-radio>
            <webmodule-radio value="3" disabled>Grape (disabled)</webmodule-radio>
          </webmodule-radio-group>
        </div>`
    })}
    ${codeExample({
      label: 'Radio buttons',
      description: 'The same group semantics rendered as a segmented control.',
      code: `
        <div class="demo-stack">
          <webmodule-radio-group label="Size small" name="fruit-c" value="1" size="small">
            <webmodule-radio-button value="1">Apple</webmodule-radio-button>
            <webmodule-radio-button value="2">Orange</webmodule-radio-button>
            <webmodule-radio-button value="3">Grape</webmodule-radio-button>
          </webmodule-radio-group>
          <webmodule-radio-group label="Size medium" name="fruit-d" value="2" size="medium">
            <webmodule-radio-button value="1">Apple</webmodule-radio-button>
            <webmodule-radio-button value="2">Orange</webmodule-radio-button>
            <webmodule-radio-button value="3">Grape</webmodule-radio-button>
          </webmodule-radio-group>
          <webmodule-radio-group label="Size large" name="fruit-e" value="3" size="large">
            <webmodule-radio-button value="1">Apple</webmodule-radio-button>
            <webmodule-radio-button value="2">Orange</webmodule-radio-button>
            <webmodule-radio-button value="3">Grape</webmodule-radio-button>
          </webmodule-radio-group>
        </div>`
    })}
    ${codeExample({
      label: 'Label placement',
      code: `
        <div style="max-width: 520px;">
          <webmodule-radio-group class="webmodule-control-left-label" label="Left label:" name="fruit-f" value="1" inline size="small">
            <webmodule-radio value="1">Apple</webmodule-radio>
            <webmodule-radio value="2">Orange</webmodule-radio>
            <webmodule-radio value="3">Grape</webmodule-radio>
          </webmodule-radio-group>
        </div>`
    })}
  `
};

export const toggleSection: DemoSection = {
  id: 'toggle',
  title: 'Toggle',
  tags: ['webmodule-toggle'],
  summary: 'A switch for immediate on/off state, as an alternative to a checkbox.',
  render: () => html`
    ${codeExample({
      label: 'Sizes and states',
      code: `
        <div class="demo-stack">
          <webmodule-toggle size="small">Small</webmodule-toggle>
          <webmodule-toggle size="medium">Medium</webmodule-toggle>
          <webmodule-toggle size="large">Large</webmodule-toggle>
          <webmodule-toggle checked>Checked</webmodule-toggle>
          <webmodule-toggle disabled>Disabled</webmodule-toggle>
        </div>`
    })}
  `
};
